import { normalizeTextToWords, SPOKEN_NUMBER_WORDS_MAP } from '../../picklist/utils';
import {
  PICKLIST_COMMAND_ALIAS_SET_LOWER,
  PICKLIST_COMMAND_WORD_SET_LOWER,
} from '../NvoqQueue/utils/commandUtils';
import { resolvePicklistSelectionMatchCandidates } from './picklistSelectionMatchCandidates';
import { resolvePicklistSelectionDecisionRule } from './picklistSelectionDecisionRule';
import {
  EXACT_CONFIDENCE_THRESHOLD,
  FUZZY_CONFIDENCE_THRESHOLD,
  FUZZY_MARGIN_THRESHOLD,
  PicklistSelectionCommandMode,
  PicklistSelectionDecisionRule,
  PicklistSelectionMatchType,
  type GetPicklistOptionSelectionFuzzyMatch,
  type GetPicklistOptionSelectionMatchOptions,
  type PicklistSelectionMatchCandidate,
  type PicklistSelectionMatchCandidates,
} from './picklistSelectionDecision.shared';
import { PicklistSelectionSource } from '../usePicklistDictationSelection';
export {
  EXACT_CONFIDENCE_THRESHOLD,
  FUZZY_CONFIDENCE_THRESHOLD,
  FUZZY_MARGIN_THRESHOLD,
  PicklistSelectionCommandMode,
  PicklistSelectionDecisionRule,
  PicklistSelectionMatchType,
} from './picklistSelectionDecision.shared';
export type {
  GetPicklistOptionSelectionFuzzyMatch,
  GetPicklistOptionSelectionMatchOptions,
  PicklistSelectionMatchCandidate,
  PicklistSelectionMatchCandidates,
  PicklistOptionSelectionFuzzyMatch,
} from './picklistSelectionDecision.shared';
export { calculateFuzzyScore, resolveBestFuzzyPicklistOptionMatch } from './picklistFuzzyScoring';
export type {
  BestFuzzyPicklistOptionMatch,
  PicklistOptionForFuzzyMatch,
} from './picklistFuzzyScoring';
export { resolvePicklistSelectionDecisionRule } from './picklistSelectionDecisionRule';
export { resolvePicklistSelectionMatchCandidates } from './picklistSelectionMatchCandidates';

// Dedupe only consecutive duplicate resolution emissions within the same dictation stream.
const lastPicklistResolutionEmissionKeyByDictationId = new Map<string, string>();

/**
 * Returns true only when this resolution key differs from the last emitted key for the same
 * dictation id. Used to suppress duplicate logs/metrics from repeated processing of the same chunk.
 */
export const shouldEmitPicklistResolution = ({
  dictationId,
  emissionKey,
}: {
  dictationId: string;
  emissionKey: string;
}): boolean => {
  const lastKey = lastPicklistResolutionEmissionKeyByDictationId.get(dictationId);
  if (lastKey === emissionKey) {
    return false;
  }

  lastPicklistResolutionEmissionKeyByDictationId.set(dictationId, emissionKey);
  return true;
};

/**
 * Clears dedupe state for a completed dictation id so future dictations can emit normally.
 */
export const clearPicklistResolutionEmission = (dictationId: string): void => {
  lastPicklistResolutionEmissionKeyByDictationId.delete(dictationId);
};

/**
 * Classifies command style from the matched selection text:
 * - trigger + number -> `numeric`
 * - trigger + phrase -> `name_with_trigger`
 * - phrase only -> `name_triggerless`
 */
export const resolvePicklistSelectionCommandMode = (
  matchedSelectionText: string
): PicklistSelectionCommandMode => {
  const words = normalizeTextToWords(matchedSelectionText);
  const commandWordIndex = words.findIndex(
    (word) =>
      PICKLIST_COMMAND_WORD_SET_LOWER.has(word) || PICKLIST_COMMAND_ALIAS_SET_LOWER.has(word)
  );

  if (commandWordIndex < 0) {
    return PicklistSelectionCommandMode.NameTriggerless;
  }

  const nextWord = words[commandWordIndex + 1];
  const numericValue = parseInt(nextWord ?? '', 10);
  if (!Number.isNaN(numericValue)) {
    return PicklistSelectionCommandMode.Numeric;
  }

  if (nextWord != null && SPOKEN_NUMBER_WORDS_MAP[nextWord] != null) {
    return PicklistSelectionCommandMode.Numeric;
  }

  return PicklistSelectionCommandMode.NameWithTrigger;
};

/**
 * Applies the decision rule to pick a source/match pair that should be executed.
 * Stable text remains preferred when both sources are acceptable.
 */
export const resolveAcceptedPicklistSelection = <Source extends string>({
  stableSelectionMatch,
  advisorSelectionMatch,
  stableMatchType,
  decisionRule,
  stableSource,
  advisorSource,
}: {
  stableSelectionMatch: PicklistSelectionMatchCandidate | null;
  advisorSelectionMatch: PicklistSelectionMatchCandidate | null;
  stableMatchType: PicklistSelectionMatchType;
  decisionRule: PicklistSelectionDecisionRule;
  stableSource: Source;
  advisorSource: Source;
}): {
  selectionSource: Source | null;
  selectionMatch: PicklistSelectionMatchCandidate | null;
} => {
  // Only these rules are directly actionable.
  const isAcceptedDecision =
    decisionRule === PicklistSelectionDecisionRule.Consensus ||
    decisionRule === PicklistSelectionDecisionRule.ExactOverNone ||
    (decisionRule === PicklistSelectionDecisionRule.Conflict &&
      stableMatchType === PicklistSelectionMatchType.Exact) ||
    decisionRule === PicklistSelectionDecisionRule.GuardedFuzzy;

  if (isAcceptedDecision) {
    if (stableSelectionMatch != null) {
      // Stable text remains primary when both sources are accepted.
      return {
        selectionSource: stableSource,
        selectionMatch: stableSelectionMatch,
      };
    }

    if (advisorSelectionMatch != null) {
      return {
        selectionSource: advisorSource,
        selectionMatch: advisorSelectionMatch,
      };
    }
  }

  return {
    selectionSource: null,
    selectionMatch: null,
  };
};

/**
 * Maps a picked index (and optional fuzzy flag) to the telemetry match type enum.
 */
export const resolvePicklistSelectionMatchType = ({
  pickIndex,
  isFuzzy = false,
}: {
  pickIndex: number | null;
  isFuzzy?: boolean;
}): PicklistSelectionMatchType => {
  if (pickIndex == null) {
    return PicklistSelectionMatchType.None;
  }

  return isFuzzy ? PicklistSelectionMatchType.Fuzzy : PicklistSelectionMatchType.Exact;
};

export type ResolvePicklistSelectionDecisionResult = PicklistSelectionMatchCandidates & {
  decisionRule: PicklistSelectionDecisionRule;
};

/**
 * End-to-end decision helper: computes candidates and then evaluates the decision rule
 * with configurable thresholds.
 */
export const resolvePicklistSelectionDecision = ({
  text,
  picklistId,
  allowTriggerlessOptionNameMatch,
  normalizedAdvisorText,
  getPicklistOptionSelectionMatch,
  getFuzzyMatch,
  exactConfidenceThreshold = EXACT_CONFIDENCE_THRESHOLD,
  fuzzyConfidenceThreshold = FUZZY_CONFIDENCE_THRESHOLD,
  fuzzyMarginThreshold = FUZZY_MARGIN_THRESHOLD,
}: {
  text: string;
  picklistId?: string | null;
  allowTriggerlessOptionNameMatch?: boolean;
  normalizedAdvisorText: string;
  getPicklistOptionSelectionMatch: (
    text: string,
    options?: GetPicklistOptionSelectionMatchOptions
  ) => Nullable<PicklistSelectionMatchCandidate>;
  getFuzzyMatch: GetPicklistOptionSelectionFuzzyMatch;
  exactConfidenceThreshold?: number;
  fuzzyConfidenceThreshold?: number;
  fuzzyMarginThreshold?: number;
}): ResolvePicklistSelectionDecisionResult => {
  // Candidate extraction and rule resolution are split so telemetry can inspect both stages.
  const candidates = resolvePicklistSelectionMatchCandidates({
    text,
    picklistId,
    allowTriggerlessOptionNameMatch,
    normalizedAdvisorText,
    getPicklistOptionSelectionMatch,
    getFuzzyMatch,
  });

  const decisionRule = resolvePicklistSelectionDecisionRule({
    stableMatchType: candidates.stableMatchType,
    stablePickIndex: candidates.stablePickIndex,
    stableConfidence: candidates.stableConfidence,
    stableMargin: candidates.stableMargin,
    advisorMatchType: candidates.advisorMatchType,
    advisorPickIndex: candidates.advisorPickIndex,
    advisorConfidence: candidates.advisorConfidence,
    advisorMargin: candidates.advisorMargin,
    exactConfidenceThreshold,
    fuzzyConfidenceThreshold,
    fuzzyMarginThreshold,
  });

  return {
    ...candidates,
    decisionRule,
  };
};

export type PicklistSelectionResolutionTelemetry<Source extends string = string> = Readonly<{
  // Which input stream ultimately produced the selected picklist option.
  source: Source;
  // Final decision rule classification for this resolution attempt.
  decisionRule: PicklistSelectionDecisionRule;
  // Convenience flag for dashboards that only need agreement vs non-agreement.
  hasConsensus: boolean;
  // Match quality for stable text (`exact`, `fuzzy`, `none`).
  stableMatchType: PicklistSelectionMatchType;
  // Match quality for advisor text (`exact`, `fuzzy`, `none`).
  advisorMatchType: PicklistSelectionMatchType;
  // Pick index resolved from stable text, or `null` if no stable match.
  stablePickIndex: number | null;
  // Pick index resolved from advisor text, or `null` if no advisor match.
  advisorPickIndex: number | null;
  // Input text used for the selection that was actually applied.
  selectedInputText: string;
  // Raw stable text considered during resolution.
  stableText: string;
  // Stable-text phrase that actually matched a picklist option (null when no stable match).
  stablePickText: string | null;
  // Advisor text considered during resolution (null only when advisor transcript is unavailable).
  advisorText: string | null;
  // Advisor phrase that actually matched a picklist option (null when no advisor match).
  advisorPickText: string | null;
  // How the command was phrased by the user.
  commandMode: PicklistSelectionCommandMode;
  // True when either source contributed a fuzzy match.
  fuzzyUsed: boolean;
  // True when the selected source came from the advisor transcript.
  advisorUsed: boolean;
}>;

/**
 * Builds the normalized telemetry payload used for both logs and analytics.
 */
export const buildPicklistSelectionResolutionTelemetry = <Source extends string>({
  source,
  decisionRule,
  stablePickIndex,
  advisorPickIndex,
  stableMatchType,
  advisorMatchType,
  selectedInputText,
  stableText,
  stablePickText,
  advisorText,
  advisorPickText,
  commandMode,
}: {
  source: Source;
  decisionRule: PicklistSelectionDecisionRule;
  stablePickIndex: number | null;
  advisorPickIndex: number | null;
  stableMatchType: PicklistSelectionMatchType;
  advisorMatchType: PicklistSelectionMatchType;
  selectedInputText: string;
  stableText: string;
  stablePickText: string | null;
  advisorText: string | null;
  advisorPickText: string | null;
  commandMode: PicklistSelectionCommandMode;
}): PicklistSelectionResolutionTelemetry<Source> => ({
  source,
  decisionRule,
  hasConsensus: decisionRule === PicklistSelectionDecisionRule.Consensus,
  stableMatchType,
  advisorMatchType,
  stablePickIndex,
  advisorPickIndex,
  selectedInputText,
  stableText,
  stablePickText,
  advisorText,
  advisorPickText,
  commandMode,
  fuzzyUsed:
    stableMatchType === PicklistSelectionMatchType.Fuzzy ||
    advisorMatchType === PicklistSelectionMatchType.Fuzzy,
  advisorUsed: source === PicklistSelectionSource.WebSpeechAdvisor,
});
